import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { convertNumberToWords } from "./utility";

function mapSeller(s) {
    return {
        name: s?.companyName || s?.name || "",
        subtitle: s?.companySubtitle || s?.subtitle || "",
    };
}

function buildFooterLines(fromSeller) {
    const s = fromSeller || {};
    const address = s.companyAddress || s.address || null;
    const phones = [s.companyPhone1 || s.phone1, s.companyPhone2 || s.phone2].filter(Boolean).join(" - ");
    const email = s.companyEmail || s.email || null;
    const line1 = [address, phones || null, email].filter(Boolean).join(" - ");

    const regs = [
        s.companyIce || s.ice ? `ICE: ${s.companyIce || s.ice}` : null,
        s.companyRc || s.rc ? `RC: ${s.companyRc || s.rc}` : null,
        s.companyPatente || s.patente ? `Patente: ${s.companyPatente || s.patente}` : null,
        s.companyIf || s.if ? `IF: ${s.companyIf || s.if}` : null,
        s.companyCnss || s.cnss ? `CNSS: ${s.companyCnss || s.cnss}` : null,
    ].filter(Boolean).join(" - ");

    return { line1, regs };
}

const normalizeText = (value, fallback = "-") => {
    if (value === null || value === undefined || value === "") {
        return fallback;
    }

    return String(value).normalize("NFKC").replace(/\s+/g, " ").trim();
};

const formatDateFr = (value) => {
    if (!value) return "-";

    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
        return normalizeText(value, "-");
    }

    return date.toLocaleDateString("fr-FR");
};

const formatMoney = (value) =>
    Number(value || 0).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, " ");

const getPaymentModeLabel = (mode) => {
    switch (mode) {
        case "ESPECES":
            return "Especes";
        case "CHEQUE":
            return "Cheque";
        case "VIREMENT":
            return "Virement";
        case "TRAITE":
            return "Traite";
        case "CARTE_BANCAIRE":
            return "Carte bancaire";
        default:
            return normalizeText(mode, "-");
    }
};

export const generateSupplierInvoicePdf = (
    invoice,
    {
        company = {},
        openInNewTab = true,
        reglements = [],
    } = {}
) => {
    const doc = new jsPDF({ unit: "mm", format: "a4" });
    const seller = mapSeller(company);
    const { line1: footerLine1, regs: footerLine2 } = buildFooterLines(company);

    const PAGE_W = doc.internal.pageSize.width;
    const PAGE_H = doc.internal.pageSize.height;
    const M = 20;
    const FOOTER_H = 22;

    const BRAND = {
        primary: [6, 145, 43],
        ink: [15, 23, 42],
        subtle: [100, 116, 139],
        line: [226, 232, 240],
        headerBg: [241, 245, 249],
        cardBg: [248, 250, 252],
    };

    const amountToWords = (n) => convertNumberToWords(Math.round(n || 0)).toUpperCase() + " DIRHAMS";

    const drawHeader = () => {
        doc.setFont("helvetica", "bold");
        doc.setFontSize(22);
        doc.setTextColor(...BRAND.primary);
        doc.text(seller.name || "MA SOCIETE", PAGE_W / 2, 22, { align: "center" });

        if (seller.subtitle) {
            doc.setFontSize(10);
            doc.text(seller.subtitle, PAGE_W / 2, 29, { align: "center" });
        }

        doc.setDrawColor(...BRAND.line);
        doc.line(M, 34, PAGE_W - M, 34);
    };

    const CARD_W = (PAGE_W - M * 3) / 2;
    const CARD_Y = 40;

    const getCardHeight = (lines) => {
        const lineH = 4.8;
        const innerW = CARD_W - 10;
        const wrappedLines = (lines || []).flatMap((line) => {
            if (!line) return [];
            return doc.splitTextToSize(String(line), innerW);
        });
        return Math.max(28, 14 + wrappedLines.length * lineH + 4);
    };

    const drawCard = (x, title, lines, cardH) => {
        const lineH = 4.8;
        const innerW = CARD_W - 10;
        let cursorY = CARD_Y + 14;

        doc.setDrawColor(...BRAND.line);
        doc.setFillColor(...BRAND.cardBg);
        doc.roundedRect(x, CARD_Y, CARD_W, cardH, 3, 3, "FD");

        doc.setFont("helvetica", "bold");
        doc.setFontSize(11);
        doc.setTextColor(...BRAND.ink);
        doc.text(title, x + 5, CARD_Y + 7);

        doc.setFont("helvetica", "normal");
        doc.setFontSize(9);
        doc.setTextColor(...BRAND.subtle);

        (lines || []).forEach((line) => {
            if (!line) return;
            const wrapped = doc.splitTextToSize(String(line), innerW);
            doc.text(wrapped, x + 5, cursorY);
            cursorY += wrapped.length * lineH;
        });
    };

    drawHeader();

    const leftCardLines = [
        `N° Facture: ${normalizeText(invoice.numeroFactfournisseur)}`,
        `Date facturation: ${formatDateFr(invoice.dateFacture)}`,
        // invoice.referenceBons
        //     ? `Bons de reception : ${normalizeText(invoice.referenceBons)}`
        //     : null,
    ];

    const rightCardLines = [
        normalizeText(invoice.fournisseurNom),
        normalizeText(invoice.fournisseurAdresse, ""),
        invoice.fournisseurIce ? `ICE : ${normalizeText(invoice.fournisseurIce)}` : null,
        invoice.fournisseurTelephone ? `Tel : ${normalizeText(invoice.fournisseurTelephone)}` : null,
        invoice.fournisseurEmail ? `Email : ${normalizeText(invoice.fournisseurEmail)}` : null,
    ];

    const commonCardH = Math.max(getCardHeight(leftCardLines), getCardHeight(rightCardLines));
    drawCard(M, "FACTURE FOURNISSEUR", leftCardLines, commonCardH);
    drawCard(M * 2 + CARD_W, "FOURNISSEUR", rightCardLines, commonCardH);

    const tableY = CARD_Y + commonCardH + 8;

    autoTable(doc, {
        startY: tableY,
        head: [["Code", "Article", "Qte", "UM", "PU HT", "Total HT"]],
        body: (invoice.lignes || []).map((l) => [
            normalizeText(l.articleCode),
            normalizeText(l.articleNom),
            normalizeText(l.quantite),
            normalizeText(l.unite, "U"),
            formatMoney(l.prixUnitaireHT),
            formatMoney(l.montantHT),
        ]),
        styles: { fontSize: 9, cellPadding: 3, overflow: "linebreak" },
        headStyles: {
            fillColor: BRAND.headerBg,
            textColor: BRAND.ink,
            fontStyle: "bold",
        },
        columnStyles: {
            0: { cellWidth: 24 },
            1: { cellWidth: 70 },
            2: { cellWidth: 16, halign: "center" },
            3: { cellWidth: 16, halign: "center" },
            4: { cellWidth: 26, halign: "right" },
            5: { cellWidth: 26, halign: "right" },
        },
        margin: { left: M, right: M, bottom: 30 },
    });

    let y = doc.lastAutoTable.finalY + 10;
    const totalHT = Number(invoice.montantHT || 0);
    const totalTVA = Number(invoice.montantTVA || 0);
    const totalTTC = Number(invoice.montantTTC || 0);
    const totalPaye = reglements.reduce(
        (sum, reglement) => sum + Number(reglement?.montant ?? reglement?.montantRegle ?? 0),
        0
    ) || Number(invoice.montantRegle || 0);
    const resteAPayer = Math.max(totalTTC - totalPaye, 0);
    const totalsBoxHeight = 44;
    const totalsLineStart = 8;
    const totalsLineGap = 8;
    const afterTotalsGap = 10;

    doc.setFillColor(245, 245, 245);
    doc.roundedRect(PAGE_W - M - 70, y, 70, totalsBoxHeight, 3, 3, "F");

    doc.setFont("helvetica", "normal");
    doc.setFontSize(9);
    doc.setTextColor(...BRAND.ink);

    doc.text("Total HT", PAGE_W - M - 65, y + totalsLineStart);
    doc.text(formatMoney(totalHT), PAGE_W - M - 5, y + totalsLineStart, { align: "right" });

    doc.text("TVA (20%)", PAGE_W - M - 65, y + totalsLineStart + totalsLineGap);
    doc.text(formatMoney(totalTVA), PAGE_W - M - 5, y + totalsLineStart + totalsLineGap, { align: "right" });

    doc.setFont("helvetica", "bold");
    doc.text("Total TTC", PAGE_W - M - 65, y + totalsLineStart + (totalsLineGap * 2));
    doc.text(formatMoney(totalTTC), PAGE_W - M - 5, y + totalsLineStart + (totalsLineGap * 2), { align: "right" });

    doc.setFont("helvetica", "normal");
    doc.text("Payé", PAGE_W - M - 65, y + totalsLineStart + (totalsLineGap * 3));
    doc.text(formatMoney(totalPaye), PAGE_W - M - 5, y + totalsLineStart + (totalsLineGap * 3), { align: "right" });

    doc.setFont("helvetica", "bold");
    doc.text("Reste à payer", PAGE_W - M - 65, y + totalsLineStart + (totalsLineGap * 4));
    doc.text(formatMoney(resteAPayer), PAGE_W - M - 5, y + totalsLineStart + (totalsLineGap * 4), { align: "right" });

    const totalsBottomY = y + totalsBoxHeight;
    let nextSectionY = totalsBottomY + afterTotalsGap;

    if (reglements.length > 0) {
        autoTable(doc, {
            startY: nextSectionY,
            head: [["Réglement", "Montant", "Mode", "Numero piece"]],
            body: reglements.map((reglement) => [
                formatDateFr(reglement?.dateReglement),
                formatMoney(reglement?.montant ?? reglement?.montantRegle),
                getPaymentModeLabel(reglement?.mode || reglement?.modeReglement),
                normalizeText(
                    reglement?.numeroPiece || reglement?.referencePaiement || reglement?.reference,
                    "-"
                ),
            ]),
            styles: { fontSize: 8.5, cellPadding: 3, overflow: "linebreak" },
            headStyles: {
                fillColor: BRAND.headerBg,
                textColor: BRAND.ink,
                fontStyle: "bold",
            },
            columnStyles: {
                0: { cellWidth: 24 },
                1: { cellWidth: 22, halign: "right" },
                2: { cellWidth: 26 },
                3: { cellWidth: 38 },
            },
            margin: { left: PAGE_W - M - 110, right: M, bottom: 30 },
        });
        nextSectionY = doc.lastAutoTable.finalY + 8;
    }

    doc.setFontSize(9);
    doc.setFont("helvetica", "italic");
    doc.setTextColor(...BRAND.subtle);
    const amountInWords = doc.splitTextToSize(
        `Arrete la presente facture fournisseur a la somme de : ${amountToWords(totalTTC)}`,
        PAGE_W - (M * 2)
    );
    doc.text(amountInWords, M, nextSectionY + 4);

    function drawFooter() {
        doc.setDrawColor(...BRAND.line);
        doc.line(M, PAGE_H - FOOTER_H, PAGE_W - M, PAGE_H - FOOTER_H);

        doc.setFontSize(8);
        doc.setTextColor(80);

        if (footerLine1) {
            doc.text(footerLine1, PAGE_W / 2, PAGE_H - 12, { align: "center" });
        }
        if (footerLine2) {
            doc.text(footerLine2, PAGE_W / 2, PAGE_H - 7, { align: "center" });
        }
    }

    const totalPages = doc.getNumberOfPages();
    for (let page = 1; page <= totalPages; page += 1) {
        doc.setPage(page);
        drawFooter();
    }

    const fileName = `Facture-Fournisseur-${invoice.numero}.pdf`;

    if (openInNewTab) {
        window.open(doc.output("bloburl"), "_blank");
    } else {
        doc.save(fileName);
    }
};
